// ============================================================
//  ALTIMO Medical Center — PrescripcionesPanel.jsx
//  Panel compartido: el médico indica medicamento desde el
//  expediente o desde Formularios Clínicos. NO descuenta stock —
//  eso ocurre cuando Farmacia dispensa (prescripciones:dispensar).
//  Reutilizado por: ExpedientePaciente.jsx, Formularios.jsx
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import { useConfirmar } from './ModalConfirmar';

const S = {
  card:   { background: '#fff', borderRadius: '10px', border: '1px solid #dde3ea', padding: '18px', marginBottom: '14px' },
  fila2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  titulo: {
    fontSize: '12px', fontWeight: '700', color: '#1B2A5E',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '14px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  input:  { width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #c0ccda', borderRadius: '7px', background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box' },
  label:  { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.04em' },
  campo:  { marginBottom: '12px' },
  tabla:  { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:     { textAlign: 'left', padding: '8px 12px', fontSize: '11px', color: '#6c757d', fontWeight: '600', borderBottom: '2px solid #e8f0fb', background: '#f8fafd' },
  td:     { padding: '9px 12px', borderBottom: '1px solid #eef1f6', color: '#333' },
  btnPrimario:   { background: '#1B2A5E', color: '#fff', border: 'none', borderRadius: '7px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  btnVerde:      { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnSecundario: { background: '#fff', color: '#495057', border: '1px solid #c0ccda', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer' },
  pill: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '99px',
    fontSize: '10px', fontWeight: '700',
    background: { verde: '#d4edda', amber: '#fff3cd', azul: '#cce5ff', rojo: '#f8d7da', gris: '#e2e3e5' }[color] || '#e2e3e5',
    color:      { verde: '#155724', amber: '#856404', azul: '#004085', rojo: '#721c24', gris: '#383d41' }[color] || '#333',
  }),
};

const ESTADO_PRESC = {
  Pendiente:            { color:'amber', label:'⏳ Pendiente de dispensar' },
  Dispensado:           { color:'verde', label:'✔ Dispensado'             },
  Devolucion_Pendiente: { color:'azul',  label:'↩ Devolución solicitada'  },
  Devuelto_Parcial:     { color:'azul',  label:'↩ Devuelto parcial'       },
  Devuelto_Total:       { color:'gris',  label:'↩ Devuelto total'         },
  Cancelado:            { color:'rojo',  label:'✕ Cancelado'              },
};

// Props:
//  admision_id  (requerido) — id de la admisión activa
//  medico_id    (opcional)  — médico tratante de esa admisión, para prellenar
//  usuario      (requerido) — usuario logueado (quien registra la acción)
export default function PrescripcionesPanel({ admision_id, medico_id, usuario }) {
  const [prescripciones, setPrescripciones] = useState([]);
  const [cargando,       setCargando]       = useState(true);
  const [mostrarForm,    setMostrarForm]    = useState(false);
  const [alergia,        setAlergia]        = useState({ tiene:false, detalle:'' });

  // ── Formulario nueva prescripción
  const [busqMed,        setBusqMed]        = useState('');
  const [resMed,         setResMed]         = useState([]);
  const [medSel,         setMedSel]         = useState(null);
  const [cantidad,       setCantidad]       = useState('1');
  const [indicaciones,   setIndicaciones]   = useState('');
  const [numReceta,      setNumReceta]      = useState('');
  const [drPrescriptor,  setDrPrescriptor]  = useState('');
  const [guardando,      setGuardando]      = useState(false);
  const [error,          setError]          = useState('');
  const { confirmar, ModalConfirmarUI } = useConfirmar();

  const cargarPrescripciones = useCallback(async () => {
    if (!admision_id) { setCargando(false); return; }
    setCargando(true);
    try {
      const r = await api.prescripciones.listar({ admision_id });
      if (r?.ok) setPrescripciones(r.data);
    } catch(e) { console.warn('[prescripciones] Error cargando:', e.message); }
    setCargando(false);
  }, [admision_id]);

  useEffect(() => { cargarPrescripciones(); }, [cargarPrescripciones]);

  // Alergias declaradas en la Hoja de ingreso y egreso — se muestran como
  // alerta visible para que el médico las tenga en cuenta antes de prescribir.
  useEffect(() => {
    if (!admision_id) { setAlergia({ tiene:false, detalle:'' }); return; }
    api.expedientes.alergias(admision_id)
      .then(r => { if (r?.ok) setAlergia(r.data); })
      .catch(() => setAlergia({ tiene:false, detalle:'' }));
  }, [admision_id]);

  // Búsqueda de medicamento en inventario (con debounce simple)
  useEffect(() => {
    if (busqMed.trim().length < 2) { setResMed([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.inventario.buscar(busqMed);
        if (r?.ok) setResMed(r.data.slice(0, 8));
      } catch(_) { setResMed([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqMed]);

  const seleccionarMed = (m) => {
    setMedSel(m);
    setBusqMed(m.nombre);
    setResMed([]);
    if (!m.es_controlado) { setNumReceta(''); setDrPrescriptor(''); }
  };

  const limpiarForm = () => {
    setBusqMed(''); setMedSel(null); setCantidad('1');
    setIndicaciones(''); setNumReceta(''); setDrPrescriptor(''); setError('');
  };

  const guardarPrescripcion = async () => {
    setError('');
    const erroresValidacion = [];

    if (!admision_id)                  erroresValidacion.push('No hay admisión activa para este paciente.');
    if (!medSel)                       erroresValidacion.push('Selecciona un medicamento del inventario.');
    if (!cantidad || Number(cantidad) <= 0) erroresValidacion.push('Ingresa una cantidad válida.');
    if (!indicaciones.trim())          erroresValidacion.push('Las indicaciones de uso (dosis, frecuencia, vía) son obligatorias.');
    if (medSel?.es_controlado) {
      if (!numReceta.trim())     erroresValidacion.push('Medicamento controlado: ingresa el número de receta.');
      if (!drPrescriptor.trim()) erroresValidacion.push('Medicamento controlado: ingresa el médico prescriptor.');
    }

    if (erroresValidacion.length > 0) {
      setError(erroresValidacion.length === 1
        ? erroresValidacion[0]
        : `Hay ${erroresValidacion.length} campos por corregir:\n` + erroresValidacion.map(e => `• ${e}`).join('\n'));
      return;
    }
    setGuardando(true);
    try {
      const r = await api.prescripciones.crear({
        admision_id,
        inventario_id:     medSel.id,
        medico_id:         medico_id || null,
        descripcion:       medSel.nombre,
        cantidad_recetada: Number(cantidad),
        indicaciones:      indicaciones.trim(),
        num_receta:        medSel.es_controlado ? numReceta.trim()     : null,
        dr_prescriptor:    medSel.es_controlado ? drPrescriptor.trim() : null,
        usuario_id:        usuario.id,
      });
      if (r?.ok) {
        limpiarForm();
        setMostrarForm(false);
        cargarPrescripciones();
      } else {
        setError(r?.error || 'No se pudo guardar la prescripción.');
      }
    } catch(e) { setError(e.message); }
    setGuardando(false);
  };

  const cancelarPrescripcion = async (p) => {
    if (!(await confirmar({
      texto: `¿Cancelar la prescripción de "${p.descripcion}"? Aún no ha sido dispensada.`,
      tipo: 'advertencia',
      textoConfirmar: 'Cancelar prescripción',
    }))) return;
    const r = await api.prescripciones.cancelar({
      id: p.id, motivo: 'Cancelada por el médico', usuario_id: usuario.id,
    });
    if (r?.ok) cargarPrescripciones();
    else alert(r?.error || 'No se pudo cancelar.');
  };

  if (!admision_id) {
    return (
      <div style={S.card}>
        <div style={S.titulo}><span>💊 Prescripciones de Medicamentos</span></div>
        <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
          Este paciente no tiene una admisión activa. Solo se pueden recetar medicamentos a pacientes hospitalizados.
        </div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      {ModalConfirmarUI}
      <div style={S.titulo}>
        <span>💊 Prescripciones de Medicamentos ({prescripciones.length})</span>
        <button style={S.btnVerde} onClick={() => setMostrarForm(v => !v)}>
          {mostrarForm ? 'Cerrar' : '+ Nueva prescripción'}
        </button>
      </div>

      {/* ── Alerta de alergias declaradas (Hoja de ingreso y egreso) ──
          Siempre visible mientras haya una alergia registrada, para que
          el médico la tenga presente incluso antes de abrir el formulario. */}
      {alergia.tiene && (
        <div style={{ background:'#fff3cd', border:'2px solid #dc3545', borderRadius:'8px',
          padding:'10px 14px', marginBottom:'14px', display:'flex', alignItems:'flex-start', gap:'10px' }}>
          <span style={{ fontSize:'18px' }}>⚠️</span>
          <div>
            <div style={{ fontSize:'12px', fontWeight:800, color:'#721c24', textTransform:'uppercase', letterSpacing:'.03em' }}>
              Paciente alérgico — verificar antes de prescribir
            </div>
            <div style={{ fontSize:'13px', color:'#721c24', marginTop:'2px' }}>
              {alergia.detalle || 'Alergia declarada en la hoja de ingreso y egreso (sin detalle especificado).'}
            </div>
          </div>
        </div>
      )}

      {/* ── Formulario nueva prescripción ── */}
      {mostrarForm && (
        <div style={{ background:'#f8fafd', border:'1px solid #dde3ea', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
          {alergia.tiene && (
            <div style={{ background:'#fff', border:'1.5px solid #dc3545', borderRadius:'7px',
              padding:'8px 12px', marginBottom:'12px', fontSize:'12px', color:'#721c24', fontWeight:600 }}>
              ⚠️ Recuerda: paciente alérgico a <strong>{alergia.detalle || 'lo declarado en ingreso/egreso'}</strong>. Verifica el medicamento antes de continuar.
            </div>
          )}
          <div style={S.campo}>
            <label style={S.label}>Medicamento *</label>
            <input style={S.input} placeholder="Buscar por nombre o código..."
              value={busqMed} onChange={e => { setBusqMed(e.target.value); setMedSel(null); }} />
            {resMed.length > 0 && (
              <div style={{ border:'1px solid #dde3ea', borderRadius:'7px', marginTop:'4px', maxHeight:'180px', overflowY:'auto' }}>
                {resMed.map(m => (
                  <div key={m.id} onClick={() => seleccionarMed(m)}
                    style={{ padding:'8px 10px', cursor:'pointer', fontSize:'12px', borderBottom:'1px solid #eef1f6',
                      display:'flex', justifyContent:'space-between' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f0f7ff'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <span>
                      {m.nombre} {m.es_controlado === 1 && <span style={{ color:'#7c3aed', fontWeight:700 }}> 🔒</span>}
                    </span>
                    <span style={{ color:'#6c757d' }}>Stock: {m.stock} {m.unidad}</span>
                  </div>
                ))}
              </div>
            )}
            {medSel && (
              <div style={{ fontSize:'11px', color:'#1D9E75', marginTop:'4px', fontWeight:600 }}>
                ✔ Seleccionado: {medSel.nombre} (stock disponible: {medSel.stock} {medSel.unidad})
              </div>
            )}
          </div>

          <div style={S.fila2}>
            <div style={S.campo}>
              <label style={S.label}>Cantidad a recetar *</label>
              <input style={S.input} type="number" min="0.01" step="any"
                value={cantidad} onChange={e => setCantidad(e.target.value)} />
            </div>
            <div style={S.campo}>
              <label style={S.label}>Indicaciones de uso *</label>
              <input style={S.input} placeholder="Ej: cada 8 horas por 5 días"
                value={indicaciones} onChange={e => setIndicaciones(e.target.value)} />
            </div>
          </div>

          {medSel?.es_controlado === 1 && (
            <div style={{ background:'#f5f3ff', border:'2px solid #7c3aed', borderRadius:'8px', padding:'12px', marginTop:'8px' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#6d28d9', marginBottom:'8px', textTransform:'uppercase' }}>
                🔒 Medicamento controlado — receta obligatoria
              </div>
              <div style={S.fila2}>
                <div style={S.campo}>
                  <label style={{ ...S.label, color:'#6d28d9' }}>Nº de receta *</label>
                  <input style={S.input} placeholder="RX-2026-00001"
                    value={numReceta} onChange={e => setNumReceta(e.target.value.toUpperCase())} />
                </div>
                <div style={S.campo}>
                  <label style={{ ...S.label, color:'#6d28d9' }}>Médico prescriptor *</label>
                  <input style={S.input} placeholder="Dr. / Dra. ..."
                    value={drPrescriptor} onChange={e => setDrPrescriptor(e.target.value.toUpperCase())} />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={{ color:'#dc3545', fontSize:'12px', fontWeight:600, marginTop:'6px', whiteSpace:'pre-line' }}>⚠ {error}</div>
          )}

          <button style={{ ...S.btnPrimario, marginTop:'10px', opacity: guardando ? 0.6 : 1 }}
            onClick={guardarPrescripcion} disabled={guardando}>
            {guardando ? 'Guardando...' : '✔ Registrar prescripción'}
          </button>
        </div>
      )}

      {/* ── Lista de prescripciones ── */}
      {cargando ? (
        <div style={{ textAlign:'center', padding:'20px', color:'#6c757d' }}>Cargando...</div>
      ) : prescripciones.length === 0 ? (
        <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
          No hay prescripciones registradas para esta admisión.
        </div>
      ) : (
        <table style={S.tabla}>
          <thead>
            <tr>
              {['Fecha','Medicamento','Cant. recetada','Dispensado','Devuelto','Estado','Receta',''].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {prescripciones.map(p => {
              const est = ESTADO_PRESC[p.estado] || { color:'gris', label:p.estado };
              return (
                <tr key={p.id}>
                  <td style={{ ...S.td, fontSize:'12px', whiteSpace:'nowrap' }}>
                    {new Date(p.creado_en).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                  </td>
                  <td style={S.td}>
                    {p.medicamento}
                    {p.es_controlado === 1 && <span style={{ color:'#7c3aed', marginLeft:'4px' }}>🔒</span>}
                    {p.indicaciones && (
                      <div style={{ fontSize:'11px', color:'#6c757d' }}>{p.indicaciones}</div>
                    )}
                  </td>
                  <td style={{ ...S.td, textAlign:'center' }}>{p.cantidad_recetada} {p.unidad}</td>
                  <td style={{ ...S.td, textAlign:'center' }}>{p.cantidad_dispensada || 0}</td>
                  <td style={{ ...S.td, textAlign:'center' }}>{p.cantidad_devuelta || 0}</td>
                  <td style={S.td}><span style={S.pill(est.color)}>{est.label}</span></td>
                  <td style={{ ...S.td, fontSize:'11px' }}>{p.num_receta || '—'}</td>
                  <td style={S.td}>
                    {p.estado === 'Pendiente' && (
                      <button style={{ ...S.btnSecundario, fontSize:'11px', padding:'4px 10px', color:'#dc3545' }}
                        onClick={() => cancelarPrescripcion(p)}>
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
